import keyBy from 'lodash/keyBy';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import { useEffect, useMemo, useState } from 'react';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { klassQueries } from '@/data/query-keys/klassQueryKey';
import { KlassPendingSummary } from '@/models/Klass';

const PENDING_SUMMARY_PAGE_SIZE = 50;

type UseKlassesPendingSummaryMapProps = {
  klassIds: number[];
};

type KlassesPendingSummaryMap = Record<number, KlassPendingSummary>;

export default function useKlassesPendingSummaryMap({
  klassIds,
}: UseKlassesPendingSummaryMapProps) {
  const [klassesPendingSummaryMap, setKlassesPendingSummaryMap] =
    useState<KlassesPendingSummaryMap>({});

  const missingIds = useMemo(
    () => sortBy(uniq(klassIds)).filter(id => !klassesPendingSummaryMap[id]),
    [klassIds, klassesPendingSummaryMap],
  );

  const {
    results: klasses,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteService({
    ...klassQueries.pendingSummaryList({
      id: missingIds,
      pageSize: PENDING_SUMMARY_PAGE_SIZE,
    }),
    enabled: missingIds.length > 0,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  useEffect(() => {
    if (hasNextPage && !isFetching) fetchNextPage({ cancelRefetch: false });
  }, [hasNextPage, isFetching, fetchNextPage]);

  useEffect(() => {
    if (hasNextPage || isFetching) return;

    setKlassesPendingSummaryMap(current => {
      const fetched = klasses.filter(({ id }) => !current[id]);

      return fetched.length ? { ...current, ...keyBy(fetched, 'id') } : current;
    });
  }, [hasNextPage, isFetching, klasses]);

  return { klassesPendingSummaryMap, isLoading: missingIds.length > 0 };
}
